import {
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  useTheme,
  IconButton,
} from "@mui/material";
import { FONT_PRIMARY, FONT_SECONDARY } from "@/theme/typography";
import { Delete } from "@mui/icons-material";
import useResponsive from "@/hooks/useResponsive";
import Counter from "@/components/Counter";
import {
  cartContentStyles,
  counterStyles,
  getCartStyles,
  getImageWrapperStyles,
  imageStyles,
} from "./styles";
import {
  useAddEditCartMutation,
  useUpadateProductMutation,
} from "@/redux-store/api/marketingApi.ts";
import { useAppDispatch, useAppSelector } from "@/redux-store/hooks.ts";
import {
  removeProductFromCart,
  updateProductQuantity,
} from "@/redux-store/cart/cartSlice.ts";
import { togglePopUp } from "@/redux-store/cartPopUp/cartPopUpSlice";

interface IProps {
  product: ICartProduct;
  isCartPage: boolean;
  cartId?: string;
}

const CartCard: React.FC<IProps> = ({ cartId, product, isCartPage }) => {
  //  Theme and media
  const theme = useTheme();
  const isMobile = useResponsive("down", "md");
  const isTablet = useResponsive("between", "md", "lg");
  // product data
  const { oldPrice, realPrice, name, price, image, type, stockAvailable } =
    product;
  const [addEditCartMutation] = useAddEditCartMutation();
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((state) => state.cart.sessionId);
  const [updateProduct] = useUpadateProductMutation();
  const cartProducts = useAppSelector((state) => state.cart.products);

  const counterValue = useAppSelector((state) => {
    const productInCart = state.cart.products.find(
      (p) => p.idProduct === product.idProduct
    );
    return productInCart ? productInCart.quantity : 0;
  });

  const handleCounterChange = async (value: number) => {
    if (stockAvailable && value <= stockAvailable) {
      try {
        await addEditCartMutation({
          IdShoppingCart: cartId,
          IdProduct: product.idProduct,
          Quantity: value,
          Price: (product.realPrice || 0) * value,
          SessionId: sessionId,
        }).unwrap();
        dispatch(
          updateProductQuantity({
            idProduct: product.idProduct,
            quantity: value,
            price: product.price,
          } as ICartProduct)
        );
        try {
          await updateProduct({
            productId: product.idProduct,
            quantity: 1,
            isIncrease: value < counterValue,
          }).unwrap();
        } catch (error) {
          console.error("Error updating product:", error);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const removeProductHandler = async () => {
    try {
      // First, update the product
      await updateProduct({
        productId: product.idProduct,
        quantity: counterValue,
        isIncrease: true,
      }).unwrap();
      // Then, remove the product from the cart
      await addEditCartMutation({
        IdShoppingCart: cartId,
        IdProduct: product.idProduct,
        Quantity: 0,
        Price: 0,
        SessionId: sessionId,
      }).unwrap();
      dispatch(removeProductFromCart(product.idProduct));
      // Check if cart will be empty after removal
      if (cartProducts.length === 1) {
        dispatch(togglePopUp());
      }
    } catch (error) {
      console.error("Error removing product", error);
    }
  };

  return (
    <Card variant="outlined" sx={getCartStyles(isMobile, isCartPage)}>
      <Box
        display="flex"
        sx={{ gap: `${isMobile ? theme.spacing(1.5) : theme.spacing(4)}` }}
        mb={2}
      >
        {/* Image */}
        <Box
          sx={getImageWrapperStyles(
            isCartPage,
            type === "plan",
            isMobile,
            theme
          )}
        >
          <CardMedia
            component="img"
            image={image}
            alt={name}
            sx={imageStyles}
          />
        </Box>

        {/* Description */}
        <CardContent sx={cartContentStyles(isCartPage, isMobile, theme)}>
          <Typography
            fontSize={{ xs: !isCartPage ? 16 : 18, sm: !isCartPage ? 16 : 24 }}
            variant="h4"
            component="h3"
            color="text.primary"
            mb={0.5}
            fontFamily={!isCartPage ? FONT_SECONDARY : FONT_PRIMARY}
          >
            {type === "plan" ? (
              <>
                {name}.{" "}
                <Typography
                  fontSize="inherit !important"
                  fontFamily="inherit !important"
                  color="inherit"
                  variant="body1"
                  component="span"
                >
                  Subscription
                </Typography>
              </>
            ) : (
              name
            )}
          </Typography>

          {/* Price */}
          <Box>
            {isCartPage && oldPrice && (
              <Typography
                variant="body1"
                color="text.accent"
                component="span"
                fontWeight={700}
                mb={0.5}
              >
                Save {Math.round(((oldPrice - price) / oldPrice) * 100)}%
              </Typography>
            )}

            <Box display="flex" gap={1} alignItems="center">
              <Typography
                variant="h4"
                color={!isCartPage ? "text.accent" : "text.primary"}
                component="p"
                fontSize={
                  !isCartPage
                    ? "14px !important"
                    : isCartPage && isMobile
                    ? "1.125rem !important"
                    : isCartPage && isTablet
                    ? "1.5rem !important"
                    : "1.75rem !important"
                }
                fontWeight={700}
                fontFamily={!isCartPage ? FONT_SECONDARY : FONT_PRIMARY}
              >
                ${realPrice}
              </Typography>

              {isCartPage && oldPrice && (
                <Typography
                  variant="subtitle1"
                  fontSize={
                    isMobile
                      ? "1rem"
                      : isTablet
                      ? "1.25rem !important"
                      : "1.5rem !important"
                  }
                  color="text.secondary"
                  component="p"
                  pb={{ xs: 0.5, sm: 0.75 }}
                  sx={{ textDecoration: "line-through" }}
                >
                  ${oldPrice}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Box>

      {/* Counter and remove Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Counter
          value={counterValue}
          onChange={handleCounterChange}
          max={stockAvailable}
          sx={counterStyles}
        />
        <IconButton
          aria-label="delete"
          sx={{ color: "#909299" }}
          onClick={removeProductHandler}
        >
          <Delete fontSize="small" color="inherit" />
        </IconButton>
      </Box>
    </Card>
  );
};

export default CartCard;
